import React, { useState, useEffect, useMemo } from "react";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import handleService from "../../services/apiService.ts";
import { MethodsEnum } from "../../enums/Methods.ts";
import { useNavigate } from "react-router-dom";
import { Response } from "../../types/Api.ts";
import UpdateMedicoModal from "../../components/Modals/UpdateMedicoModal/index.tsx";
import CreateMedicoDrawer from "../../components/Modals/CreateMedicoDrawer/index.tsx";
import ConfirmDeleteDialog from "../../components/Modals/ConfirmDeleteDialog/index.tsx";
import { formatBrazilPhone } from "../../utils/phone.util.ts";
import Button from "../../ui/Button.tsx";
import SearchField from "../../ui/SearchField.tsx";
import EmptyState from "../../ui/EmptyState.tsx";
import SkeletonRows from "../../ui/SkeletonRows.tsx";
import "../../styles/design-system.css";

interface Medico {
  id: string;
  nome: string;
  email: string;
  telefone: string;
  crm: string;
}

// "Dr. João da Silva" -> "JS" (mesma regra usada no perfil do médico).
const getIniciais = (nome: string) => {
  const partes = (nome || "").replace(/^(dr|dra)\.?\s+/i, "").trim().split(/\s+/).filter(Boolean);
  if (partes.length === 0) return "?";
  const primeira = partes[0][0] || "";
  const ultima = partes.length > 1 ? partes[partes.length - 1][0] : "";
  return (primeira + ultima).toUpperCase();
};

const ListagemMedico = () => {
  const navigate = useNavigate();
  const [medicos, setMedicos] = useState<Medico[]>([]);
  const [busca, setBusca] = useState("");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isCreateOpen, setIsCreateOpen] = useState(false);
  const [currentMedico, setCurrentMedico] = useState<Medico | null>(null);
  const [medicoParaExcluir, setMedicoParaExcluir] = useState<Medico | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isExcluindo, setIsExcluindo] = useState<boolean>(false);

  useEffect(() => {
    fetchMedicos();
  }, []);

  const fetchMedicos = async () => {
    try {
      setIsLoading(true);
      const response = await handleService({ endpoint: "/medico" });
      const lista: Medico[] = Array.isArray(response) ? response : response.data || [];
      setMedicos(lista);
    } catch (error) {
    } finally {
      setIsLoading(false);
    }
  };

  const handleEditar = (medico: Medico) => {
    setIsEditModalOpen(true);
    setCurrentMedico(medico);
  };

  const handleClose = () => {
    setIsEditModalOpen(false);
    setCurrentMedico(null);
  };

  const onUpdate = (response: Response<Medico>) => {
    const medico = response.data;
    if (medico) {
      setMedicos((prev) => prev.map((c) => (c.id === medico.id ? medico : c)));
      setIsEditModalOpen(false);
    }
  };

  const onCreated = (response: Response<Medico>) => {
    const medico = response.data;
    if (medico) {
      setMedicos((prev) => [...prev, medico]);
      setIsCreateOpen(false);
    }
  };

  const handleConfirmarExclusao = async () => {
    if (!medicoParaExcluir) return;
    try {
      setIsExcluindo(true);
      await handleService({ endpoint: `/medico/${medicoParaExcluir.id}`, method: MethodsEnum.DELETE });
      setMedicos((prev) => prev.filter((m) => m.id !== medicoParaExcluir.id));
      setMedicoParaExcluir(null);
    } catch (error) {
    } finally {
      setIsExcluindo(false);
    }
  };

  const filtrados = useMemo(
    () => medicos.filter((m) => m.nome?.toLowerCase().includes(busca.toLowerCase())),
    [medicos, busca]
  );

  return (
    <div className="ds">
      <div className="ds-page-head">
        <div>
          <h1>Médicos</h1>
          <p>Visualize e gerencie os médicos da sua clínica.</p>
        </div>
        <div className="ds-page-head-actions">
          <Button variant="primary" icon={<AddCircleOutlineIcon fontSize="small" />} onClick={() => setIsCreateOpen(true)}>
            Novo médico
          </Button>
        </div>
      </div>

      <div className="ds-toolbar">
        <SearchField value={busca} onChange={setBusca} placeholder="Buscar por nome..." />
      </div>

      <div className="ds-table-wrap">
        <table className="ds-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>CRM</th>
              <th>Telefone</th>
              <th>E-mail</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <SkeletonRows columns={5} />
            ) : filtrados.length === 0 ? (
              <tr>
                <td colSpan={5}>
                  <EmptyState title="Nenhum médico encontrado" description="Cadastre o primeiro médico para começar." actionLabel="Novo médico" onAction={() => setIsCreateOpen(true)} />
                </td>
              </tr>
            ) : (
              filtrados.map((medico) => (
                <tr key={medico.id}>
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <div className="ds-avatar">{getIniciais(medico.nome)}</div>
                      <span className="ds-cell-title">{medico.nome}</span>
                    </div>
                  </td>
                  <td>{medico.crm}</td>
                  <td>{formatBrazilPhone(medico.telefone) || medico.telefone}</td>
                  <td>{medico.email}</td>
                  <td>
                    <div className="ds-row-actions">
                      <button className="ds-icon-btn" onClick={() => navigate(`/medico/${medico.id}/perfil`)} title="Visualizar">
                        <VisibilityOutlinedIcon fontSize="small" />
                      </button>
                      <button className="ds-icon-btn" onClick={() => handleEditar(medico)} title="Editar">
                        <EditOutlinedIcon fontSize="small" />
                      </button>
                      <button className="ds-icon-btn" onClick={() => setMedicoParaExcluir(medico)} title="Excluir">
                        <DeleteOutlineIcon fontSize="small" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <UpdateMedicoModal isOpen={isEditModalOpen} onClose={handleClose} medico={currentMedico} onUpdate={onUpdate} />

      <CreateMedicoDrawer open={isCreateOpen} onClose={() => setIsCreateOpen(false)} onCreated={onCreated} />

      <ConfirmDeleteDialog
        open={!!medicoParaExcluir}
        title="Excluir Médico"
        message="Tem certeza que deseja excluir este médico cadastrado? Esta ação não poderá ser desfeita."
        loading={isExcluindo}
        onCancel={() => setMedicoParaExcluir(null)}
        onConfirm={handleConfirmarExclusao}
      />
    </div>
  );
};

export default ListagemMedico;
